import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './DriverApply.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getEndpointUrl } from '../../utils/apiConfig';

const DriverApply = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      country: '',
      zipCode: '',
      quartier: ''
    },
    licenseNumber: '',
    vehicleType: 'car',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    availabilityType: 'part-time',
    preferredAreas: '',
    experienceLevel: 'none',
    documents: {
      driversLicense: null,
      driversLicenseExpiry: '',
      vehicleInsurance: null,
      vehicleInsuranceExpiry: '',
      vehicleRegistration: null,
      vehicleRegistrationExpiry: ''
    },
    backgroundCheck: false,
    additionalInfo: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prevData => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value
        }
      }));
    } else if (type === 'file' && name.startsWith('documents.')) {
      const documentType = name.split('.')[1];
      const file = files[0]; // Single file upload
      
      if (file) {
        // Validate file size (max 5MB per file)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        
        if (file.size > maxSize) {
          toast.error(t('driverApply.file_too_large', { size: (file.size / 1024 / 1024).toFixed(2) }), {
            position: "top-right",
            autoClose: 5000
          });
          // Clear the input
          e.target.value = '';
          return;
        }
        
        setFormData(prevData => ({
          ...prevData,
          documents: {
            ...prevData.documents,
            [documentType]: file
          }
        }));
      } else {
        // File was cleared
        setFormData(prevData => ({
          ...prevData,
          documents: {
            ...prevData.documents,
            [documentType]: null
          }
        }));
      }
    } else if (name.startsWith('documents.') && name.endsWith('Expiry')) {
      const docType = name.split('.')[1];
      setFormData(prevData => ({
        ...prevData,
        documents: {
          ...prevData.documents,
          [docType]: value
        }
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all 3 documents are uploaded
    const { driversLicense, driversLicenseExpiry, vehicleInsurance, vehicleInsuranceExpiry, vehicleRegistration, vehicleRegistrationExpiry } = formData.documents;
    if (!driversLicense || !vehicleInsurance || !vehicleRegistration || !driversLicenseExpiry || !vehicleInsuranceExpiry || !vehicleRegistrationExpiry) {
      const missingDocs = [];
      if (!driversLicense) missingDocs.push(t('driverApply.drivers_license'));
      if (!driversLicenseExpiry) missingDocs.push(t('driverApply.drivers_license_expiry'));
      if (!vehicleInsurance) missingDocs.push(t('driverApply.vehicle_insurance'));
      if (!vehicleInsuranceExpiry) missingDocs.push(t('driverApply.vehicle_insurance_expiry'));
      if (!vehicleRegistration) missingDocs.push(t('driverApply.vehicle_registration'));
      if (!vehicleRegistrationExpiry) missingDocs.push(t('driverApply.vehicle_registration_expiry'));
      
      toast.error(t('driverApply.missing_documents', { docs: missingDocs.join(', ') }));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData object to handle file uploads
      const applicationData = new FormData();
      
      // Add all form fields to FormData
      for (const key in formData) {
        if (key === 'address') {
          for (const addressKey in formData.address) {
            applicationData.append(`address[${addressKey}]`, formData.address[addressKey]);
          }
        } else if (key === 'documents' && formData[key]) {
          // Handle individual document files
          const { driversLicense, driversLicenseExpiry, vehicleInsurance, vehicleInsuranceExpiry, vehicleRegistration, vehicleRegistrationExpiry } = formData[key];
          if (driversLicense) applicationData.append('documents', driversLicense);
          if (driversLicenseExpiry) applicationData.append('documentsExpiry', driversLicenseExpiry);
          if (vehicleInsurance) applicationData.append('documents', vehicleInsurance);
          if (vehicleInsuranceExpiry) applicationData.append('documentsExpiry', vehicleInsuranceExpiry);
          if (vehicleRegistration) applicationData.append('documents', vehicleRegistration);
          if (vehicleRegistrationExpiry) applicationData.append('documentsExpiry', vehicleRegistrationExpiry);
        } else {
          applicationData.append(key, formData[key]);
        }
      }
      
      // Add document expiry dates as an array
      const expiryDates = [];
      if (driversLicenseExpiry) expiryDates.push(driversLicenseExpiry);
      if (vehicleInsuranceExpiry) expiryDates.push(vehicleInsuranceExpiry);
      if (vehicleRegistrationExpiry) expiryDates.push(vehicleRegistrationExpiry);
      
      // Append all expiry dates as an array
      expiryDates.forEach(date => {
        applicationData.append('documentsExpiry[]', date);
      });
      
      // Log submission for debugging
      console.log('Submitting driver application with data:', Object.fromEntries(applicationData.entries()));
      
      // Use the API URL from the config utility
      const apiUrl = getEndpointUrl('/api/application/driver');
      console.log('Using API URL:', apiUrl);
      
      const response = await axios.post(
        apiUrl, 
        applicationData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 15000, // Increased timeout for file uploads
          withCredentials: false
        }
      );
      
      console.log('Driver application submission response:', response.data);
      
      // Show success message
      toast.success(t('driverApply.application_submitted_successfully'), {
        position: "top-right",
        autoClose: 5000
      });
      
      // Reset form after submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: { street: '', city: '', country: '', zipCode: '', quartier: '' },
        licenseNumber: '',
        vehicleType: 'car',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        availabilityType: 'part-time',
        preferredAreas: '',
        experienceLevel: 'none',
        documents: {
          driversLicense: null,
          driversLicenseExpiry: '',
          vehicleInsurance: null,
          vehicleInsuranceExpiry: '',
          vehicleRegistration: null,
          vehicleRegistrationExpiry: ''
        },
        backgroundCheck: false,
        additionalInfo: ''
      });
      
      // Navigate back to home page after short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(t('driverApply.submission_problem'), {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="driver-apply-container">
      <div className="apply-header">
        <h1>{t('driverApply.become_driver')}</h1>
        <p>{t('driverApply.join_team_description')}</p>
      </div>

      <form className="driver-apply-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>{t('driverApply.personal_information')}</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('driverApply.first_name')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t('driverApply.last_name')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">{t('driverApply.email_address')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('driverApply.phone_number')}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="(\+257)?[0-9]{8}"
                placeholder={t('driverApply.phone_placeholder')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('driverApply.address_information')}</h2>
          
          <div className="form-group">
            <label htmlFor="address.street">{t('driverApply.street_address')}</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.quartier">{t('driverApply.quartier_zone')}</label>
            <input
              type="text"
              id="address.quartier"
              name="address.quartier"
              value={formData.address.quartier}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">{t('driverApply.city')}</label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="address.country">{t('driverApply.country')}</label>
              <input
                type="text"
                id="address.country"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address.zipCode">{t('driverApply.postal_code')}</label>
            <input
              type="text"
              id="address.zipCode"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>{t('driverApply.vehicle_information')}</h2>
          
          <div className="form-group">
            <label htmlFor="licenseNumber">{t('driverApply.license_number')}</label>
            <input
              type="text"
              id="licenseNumber"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="vehicleType">{t('driverApply.vehicle_type')}</label>
            <select
              id="vehicleType"
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              required
            >
              <option value="car">{t('driverApply.car')}</option>
              <option value="motorcycle">{t('driverApply.motorcycle')}</option>
              <option value="bicycle">{t('driverApply.bicycle')}</option>
              <option value="scooter">{t('driverApply.scooter')}</option>
              <option value="other">{t('driverApply.other')}</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vehicleMake">{t('driverApply.vehicle_make')}</label>
              <input
                type="text"
                id="vehicleMake"
                name="vehicleMake"
                value={formData.vehicleMake}
                onChange={handleChange}
                required
                placeholder={t('driverApply.vehicle_make_placeholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleModel">{t('driverApply.vehicle_model')}</label>
              <input
                type="text"
                id="vehicleModel"
                name="vehicleModel"
                value={formData.vehicleModel}
                onChange={handleChange}
                required
                placeholder={t('driverApply.vehicle_model_placeholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleYear">{t('driverApply.vehicle_year')}</label>
              <input
                type="text"
                id="vehicleYear"
                name="vehicleYear"
                value={formData.vehicleYear}
                onChange={handleChange}
                required
                placeholder={t('driverApply.vehicle_year_placeholder')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('driverApply.availability_experience')}</h2>
          
          <div className="form-group">
            <label htmlFor="availabilityType">{t('driverApply.availability')}</label>
            <select
              id="availabilityType"
              name="availabilityType"
              value={formData.availabilityType}
              onChange={handleChange}
              required
            >
              <option value="full-time">{t('driverApply.full_time')}</option>
              <option value="part-time">{t('driverApply.part_time')}</option>
              <option value="weekends">{t('driverApply.weekends_only')}</option>
              <option value="evenings">{t('driverApply.evenings_only')}</option>
              <option value="flexible">{t('driverApply.flexible_hours')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="preferredAreas">{t('driverApply.preferred_areas')}</label>
            <input
              type="text"
              id="preferredAreas"
              name="preferredAreas"
              value={formData.preferredAreas}
              onChange={handleChange}
              placeholder={t('driverApply.preferred_areas_placeholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="experienceLevel">{t('driverApply.delivery_experience')}</label>
            <select
              id="experienceLevel"
              name="experienceLevel"
              value={formData.experienceLevel}
              onChange={handleChange}
              required
            >
              <option value="none">{t('driverApply.no_experience')}</option>
              <option value="less-than-1">{t('driverApply.less_than_1_year')}</option>
              <option value="1-2">{t('driverApply.1_2_years')}</option>
              <option value="3-5">{t('driverApply.3_5_years')}</option>
              <option value="5+">{t('driverApply.5_plus_years')}</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('driverApply.additional_information')}</h2>
          
          <div className="documents-upload-section">
            <h3>{t('driverApply.upload_required_documents')}</h3>
            <small className="file-requirements">
              <strong>{t('driverApply.all_documents_required')}:</strong><br/>
              <em>{t('driverApply.file_requirements')}</em>
            </small>

            <div className="document-upload-grid">
              <div className="form-group document-upload">
                <label htmlFor="driversLicense">{t('driverApply.drivers_license')}</label>
                <input
                  type="file"
                  id="driversLicense"
                  name="documents.driversLicense"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.driversLicense && (
                  <div className="file-selected">
                    ✅ {formData.documents.driversLicense.name} ({(formData.documents.driversLicense.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="driversLicenseExpiry">{t('driverApply.expiration_date')}</label>
                <input
                  type="date"
                  id="driversLicenseExpiry"
                  name="documents.driversLicenseExpiry"
                  value={formData.documents.driversLicenseExpiry}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group document-upload">
                <label htmlFor="vehicleInsurance">{t('driverApply.vehicle_insurance')}</label>
                <input
                  type="file"
                  id="vehicleInsurance"
                  name="documents.vehicleInsurance"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.vehicleInsurance && (
                  <div className="file-selected">
                    ✅ {formData.documents.vehicleInsurance.name} ({(formData.documents.vehicleInsurance.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="vehicleInsuranceExpiry">{t('driverApply.expiration_date')}</label>
                <input
                  type="date"
                  id="vehicleInsuranceExpiry"
                  name="documents.vehicleInsuranceExpiry"
                  value={formData.documents.vehicleInsuranceExpiry}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group document-upload">
                <label htmlFor="vehicleRegistration">{t('driverApply.vehicle_registration')}</label>
                <input
                  type="file"
                  id="vehicleRegistration"
                  name="documents.vehicleRegistration"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.vehicleRegistration && (
                  <div className="file-selected">
                    ✅ {formData.documents.vehicleRegistration.name} ({(formData.documents.vehicleRegistration.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="vehicleRegistrationExpiry">{t('driverApply.expiration_date')}</label>
                <input
                  type="date"
                  id="vehicleRegistrationExpiry"
                  name="documents.vehicleRegistrationExpiry"
                  value={formData.documents.vehicleRegistrationExpiry}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="documents-status">
              {(() => {
                const uploadedCount = Object.values(formData.documents).filter(doc => doc !== null).length;
                if (uploadedCount === 6) {
                  return <div className="file-success">{t('driverApply.all_documents_uploaded', { count: uploadedCount })}</div>;
                } else {
                  return <div className="file-warning">{t('driverApply.upload_all_documents', { count: uploadedCount })}</div>;
                }
              })()}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="backgroundCheck"
              name="backgroundCheck"
              checked={formData.backgroundCheck}
              onChange={handleChange}
              required
            />
            <label htmlFor="backgroundCheck">{t('driverApply.agree_background_check')}</label>
          </div>

          <div className="form-group">
            <label htmlFor="additionalInfo">{t('driverApply.additional_information')}</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder={t('driverApply.additional_info_placeholder')}
              rows="3"
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('driverApply.submitting') : t('driverApply.submit_application')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DriverApply; 